import { useState } from 'react';
import {
  Text, Paper, Stack, Group, SimpleGrid, Table, Button, Modal,
  NumberInput, Textarea, Alert, RingProgress, Divider, ActionIcon,
  Tooltip, Skeleton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCreditCard, IconAlertTriangle, IconX } from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatCard from '@/components/StatCard';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import dayjs from 'dayjs';
import {
  useCreditSummary,
  useCreditApplications,
  useSubmitCreditApplication,
  useCancelCreditApplication,
} from '@/hooks/usePortalApi';

function formatCurrency(val: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
}

function utilizationColor(pct: number): string {
  if (pct > 80) return 'red';
  if (pct >= 60) return 'yellow';
  return 'green';
}

export default function CreditStatus() {
  const { data: credit, isLoading: creditLoading } = useCreditSummary();
  const { data: applications, isLoading: appsLoading } = useCreditApplications();
  const submitApplication = useSubmitCreditApplication();
  const cancelApplication = useCancelCreditApplication();

  const [modalOpen, setModalOpen] = useState(false);
  const [requestedAmount, setRequestedAmount] = useState<number | string>('');
  const [reason, setReason] = useState('');
  const [businessJustification, setBusinessJustification] = useState('');

  const handleSubmitApplication = () => {
    if (!requestedAmount || !reason) {
      notifications.show({
        title: 'Validation Error',
        message: 'Requested amount and reason are required.',
        color: 'red',
      });
      return;
    }
    submitApplication.mutate(
      {
        requested_amount: Number(requestedAmount),
        reason,
        business_justification: businessJustification || undefined,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Application Submitted',
            message: 'Your credit increase application has been submitted.',
            color: 'green',
          });
          setModalOpen(false);
          setRequestedAmount('');
          setReason('');
          setBusinessJustification('');
        },
        onError: () => {
          notifications.show({
            title: 'Error',
            message: 'Failed to submit credit application. Please try again.',
            color: 'red',
          });
        },
      },
    );
  };

  const handleCancelApplication = (id: number) => {
    cancelApplication.mutate(id, {
      onSuccess: () => {
        notifications.show({
          title: 'Application Cancelled',
          message: 'Your credit application has been cancelled.',
          color: 'green',
        });
      },
      onError: () => {
        notifications.show({
          title: 'Error',
          message: 'Failed to cancel credit application. Please try again.',
          color: 'red',
        });
      },
    });
  };

  const appItems = applications?.items ?? [];

  return (
    <Stack gap="lg">
      <PageHeader title="Credit Status" />

      {credit?.credit_hold && (
        <Alert
          icon={<IconAlertTriangle size={20} />}
          title="Credit Hold"
          color="red"
          variant="filled"
        >
          Your account is on credit hold. Please contact your account representative for assistance.
        </Alert>
      )}

      {creditLoading ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
          {[...Array(4)].map((_, i) => <Skeleton key={i} height={120} radius="md" />)}
        </SimpleGrid>
      ) : credit ? (
        <>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            <StatCard
              title="Credit Limit"
              value={formatCurrency(credit.credit_limit)}
              icon={<IconCreditCard size={24} />}
            />
            <StatCard
              title="Current Utilization"
              value={formatCurrency(credit.current_utilization)}
              icon={<IconCreditCard size={24} />}
            />
            <StatCard
              title="Available Credit"
              value={formatCurrency(credit.available_credit)}
              icon={<IconCreditCard size={24} />}
            />
            <StatCard
              title="Payment Terms"
              value={credit.payment_terms}
              icon={<IconCreditCard size={24} />}
            />
          </SimpleGrid>

          <Group gap="xl" align="flex-start">
            <Paper p="lg" shadow="sm">
              <Stack align="center" gap="xs">
                <Text size="sm" fw={700} tt="uppercase" c="dimmed">
                  Credit Utilization
                </Text>
                <RingProgress
                  size={160}
                  thickness={14}
                  roundCaps
                  label={
                    <Text ta="center" fw={700} size="lg">
                      {credit.utilization_pct.toFixed(1)}%
                    </Text>
                  }
                  sections={[
                    {
                      value: Math.min(credit.utilization_pct, 100),
                      color: utilizationColor(credit.utilization_pct),
                    },
                  ]}
                />
              </Stack>
            </Paper>

            <Paper p="lg" shadow="sm" style={{ flex: 1 }}>
              <Text size="sm" fw={700} tt="uppercase" c="dimmed" mb="sm">
                Aging Summary
              </Text>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Period</Table.Th>
                    <Table.Th ta="right">Amount</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Td>Current</Table.Td>
                    <Table.Td ta="right">
                      {formatCurrency(credit.aging_summary.current)}
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>1-30 Days</Table.Td>
                    <Table.Td ta="right">
                      {formatCurrency(credit.aging_summary['1_30'])}
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>31-60 Days</Table.Td>
                    <Table.Td ta="right">
                      {formatCurrency(credit.aging_summary['31_60'])}
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>61-90 Days</Table.Td>
                    <Table.Td ta="right">
                      {formatCurrency(credit.aging_summary['61_90'])}
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>Over 90 Days</Table.Td>
                    <Table.Td ta="right">
                      {formatCurrency(credit.aging_summary.over_90)}
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Paper>
          </Group>
        </>
      ) : (
        <EmptyState
          icon={<IconCreditCard size={30} />}
          title="No credit data"
          description="Credit information is not available at this time."
        />
      )}

      <Divider />

      <Group justify="space-between" align="center">
        <Text fw={700} size="lg">
          Credit Applications
        </Text>
        <Button
          leftSection={<IconCreditCard size={16} />}
          onClick={() => setModalOpen(true)}
        >
          Apply for Credit Increase
        </Button>
      </Group>

      {appsLoading ? (
        <Stack gap="xs">
          {[...Array(3)].map((_, i) => <Skeleton key={i} height={50} radius="md" />)}
        </Stack>
      ) : appItems.length === 0 ? (
        <EmptyState
          icon={<IconCreditCard size={30} />}
          title="No applications"
          description="You have not submitted any credit applications."
        />
      ) : (
        <Paper>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Application #</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th ta="right">Requested Amount</Table.Th>
                <Table.Th ta="right">Approved Amount</Table.Th>
                <Table.Th>Submitted</Table.Th>
                <Table.Th w={80} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {appItems.map((app) => (
                <Table.Tr key={app.id}>
                  <Table.Td>
                    <Text size="sm" style={{ fontFamily: 'monospace' }}>
                      {app.application_number}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge status={app.status} />
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={500}>
                      {formatCurrency(app.requested_amount)}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={500}>
                      {app.approved_amount != null
                        ? formatCurrency(app.approved_amount)
                        : '--'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {app.created_at
                        ? dayjs(app.created_at).format('MMM D, YYYY')
                        : '--'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {(app.status === 'submitted' || app.status === 'under_review') && (
                      <Tooltip label="Cancel application">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          onClick={() => handleCancelApplication(app.id)}
                          loading={cancelApplication.isPending}
                        >
                          <IconX size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          <Text fw={700} size="lg">
            Apply for Credit Increase
          </Text>
        }
        size="md"
      >
        <Stack gap="md">
          <NumberInput
            label="Requested Amount"
            placeholder="Enter the credit amount you are requesting"
            prefix="$"
            thousandSeparator=","
            min={0}
            value={requestedAmount}
            onChange={setRequestedAmount}
            required
          />
          <Textarea
            label="Reason"
            placeholder="Why are you requesting a credit increase?"
            minRows={3}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            required
          />
          <Textarea
            label="Business Justification"
            placeholder="Provide any additional business justification (optional)"
            minRows={3}
            value={businessJustification}
            onChange={(e) => setBusinessJustification(e.currentTarget.value)}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitApplication}
              loading={submitApplication.isPending}
            >
              Submit Application
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
